import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Space, message, Form } from 'antd';
import { ArrowRightOutlined, NotificationOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { noticesApi, show422Error, ValidationError } from '../../utils/api';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import NoticeForm from '../../components/notices/NoticeForm';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const EditNotice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await noticesApi.getEditData(id);
        const data = res?.data ?? res;
        const notice = data?.notice || {};
        let nextTeams = data?.teams || [];
        let nextEmployees = data?.employees || [];
        let nextClients = data?.clients || [];

        // اگر لیست‌ها در edit ناقص/خالی بود، از create هم بگیر تا سلکت‌ها کامل باشند
        if (!nextEmployees.length || !nextClients.length || !nextTeams.length) {
          try {
            const createRes = await noticesApi.getCreateData();
            const createData = createRes?.data ?? createRes;
            nextTeams = nextTeams.length ? nextTeams : (createData?.teams || []);
            nextEmployees = nextEmployees.length ? nextEmployees : (createData?.employees || []);
            nextClients = nextClients.length ? nextClients : (createData?.clients || []);
          } catch {
            // silently ignore fallback failure
          }
        }

        // اضافه کردن اعضای فعلی اعلان به گزینه‌ها برای جلوگیری از نمایش ID به‌جای label
        const noticeMembers = notice.member || [];
        const employeeMap = new Map((nextEmployees || []).map((e) => [String(e.id), e]));
        const clientMap = new Map((nextClients || []).map((c) => [String(c.id), c]));

        noticeMembers.forEach((m) => {
          const user = m?.user;
          if (!user?.id) return;
          const key = String(user.id);
          if (notice.to === 'client') {
            if (!clientMap.has(key)) clientMap.set(key, { id: user.id, name: user.name || `کاربر ${user.id}` });
          } else {
            if (!employeeMap.has(key)) employeeMap.set(key, { id: user.id, name: user.name || `کاربر ${user.id}` });
          }
        });

        setTeams(nextTeams);
        setEmployees(Array.from(employeeMap.values()));
        setClients(Array.from(clientMap.values()));
        const memberIds = (notice.member || []).map((m) => m.user_id);
        form.setFieldsValue({
          to: notice.to || 'employee',
          heading: notice.heading || '',
          description: notice.description || '',
          team_id: notice.department_id || undefined,
          employees: notice.to === 'employee' ? memberIds : undefined,
          clients: notice.to === 'client' ? memberIds : undefined,
        });
      } catch (e) {
        message.error(e?.message || 'خطا در دریافت اطلاعات اعلان');
        navigate('/notices');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, form, navigate]);

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await noticesApi.update(id, payload);
      message.success('اعلان بروزرسانی شد');
      navigate(`/notices/${id}`);
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, form);
      else message.error(e?.message || 'خطا در بروزرسانی اعلان');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <NotificationOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش اعلان</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">تغییرات اعلان را اعمال کنید</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate(`/notices/${id}`)}>
                بازگشت
              </Button>
              <Button type="primary" icon={<NotificationOutlined />} disabled className={PANEL_PRIMARY_BTN_CLASS}>
                راهنما
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <FormSkeleton />
            ) : (
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 custom-font">
                <NoticeForm
                  form={form}
                  submitting={submitting}
                  teams={teams}
                  employees={employees}
                  clients={clients}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate(`/notices/${id}`)}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditNotice;
